<!-- condition-rules-manager.component.html -->
@if (isLoading()) {
  <div class="flex justify-center py-12">
    <div
      class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
    ></div>
  </div>
} @else {
  <div class="space-y-6">
    <!-- Rule Configuration -->
    <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            class="text-xs font-bold text-slate-400 uppercase tracking-wider"
          >
            Rule Description
          </label>
          <input
            [ngModel]="currentConditionSet().description"
            (ngModelChange)="onDescriptionChange($event)"
            type="text"
            placeholder="Description"
            class="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl"
          />
        </div>
        <div>
          <label
            class="text-xs font-bold text-slate-400 uppercase tracking-wider"
          >
            Logical Match
          </label>
          <select
            [ngModel]="currentConditionSet().logical_operator"
            (ngModelChange)="onLogicalOperatorChange($event)"
            class="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl"
          >
            <option value="AND">Must match ALL (AND)</option>
            <option value="OR">Must match ANY (OR)</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Conditions List -->
    <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
      <div class="flex items-center justify-between mb-6">
        <h3 class="font-bold">Conditions Logic</h3>
        <button
          (click)="addCondition()"
          class="text-xs font-bold text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-lg flex items-center gap-1"
        >
          <lucide-angular [img]="Plus" class="w-4 h-4"></lucide-angular>
          Add Condition
        </button>
      </div>

      <!-- Loading State for Conditions -->
      @if (isLoadingConditions()) {
        <div class="flex justify-center items-center py-12">
          <div class="flex flex-col items-center gap-3">
            <div
              class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
            ></div>
            <span class="text-sm text-slate-500">Loading conditions...</span>
          </div>
        </div>
      } @else if (!hasConditions()) {
        <div class="text-center py-8 text-slate-400">
          No conditions added yet. Click "Add Condition" to start building
          rules.
        </div>
      } @else {
        <div class="space-y-4" cdkDropList (cdkDropListDropped)="drop($event)">
          @for (cond of currentConditionSet().conditions; track cond.id) {
            <div
              class="p-4 bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-3 group"
              cdkDrag
              [cdkDragData]="cond"
            >
              <div
                class="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500"
                cdkDragHandle
              >
                <lucide-angular
                  [img]="GripVertical"
                  class="w-5 h-5"
                ></lucide-angular>
              </div>

              <div class="flex-1 grid grid-cols-1 md:grid-cols-12 gap-3">
                <!-- Question/Element Selector -->
                <div class="md:col-span-4">
                  @if (isLoading()) {
                    <div
                      class="px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-500"
                    >
                      Loading questions...
                    </div>
                  } @else {
                    <select
                      [ngModel]="cond.condition_element_id"
                      (ngModelChange)="
                        cond.condition_element_id = $event;
                        onConditionElementChange(cond)
                      "
                      [ngModelOptions]="{ standalone: true }"
                      class="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option [ngValue]="0">Select Question...</option>
                      @for (q of availableQuestions(); track q.id) {
                        <option
                          [ngValue]="q.id"
                          [title]="getQuestionDisplayText(q)"
                        >
                          {{ getQuestionDisplayText(q) }}
                        </option>
                      }
                    </select>
                  }
                </div>

                <!-- Operator Selector -->
                <div class="md:col-span-3">
                  <select
                    [ngModel]="cond.operator"
                    (ngModelChange)="
                      cond.operator = $event; onOperatorChange(cond)
                    "
                    [ngModelOptions]="{ standalone: true }"
                    [disabled]="
                      !cond.condition_element_id ||
                      cond.condition_element_id === 0
                    "
                    class="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none disabled:bg-slate-50 disabled:text-slate-400 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Operator...</option>
                    @for (
                      op of getOperatorsForQuestion(cond.condition_element_id);
                      track op.slug
                    ) {
                      <option [value]="op.slug">{{ op.label }}</option>
                    }
                  </select>
                </div>

                <!-- Value Input -->
                <div class="md:col-span-5">
                  @if (
                    !cond.condition_element_id ||
                    cond.condition_element_id === 0
                  ) {
                    <div
                      class="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-400"
                    >
                      value
                    </div>
                  } @else {
                    @let argType = getOperatorArgType(cond.operator);

                    @if (isLoadingOptions()) {
                      <div class="flex items-center justify-center gap-2 py-2">
                        <div
                          class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"
                        ></div>
                        <span class="text-xs text-slate-500"
                          >Loading options...</span
                        >
                      </div>
                    } @else {
                      <!-- text -->
                      @if (argType === 'text') {
                        <input
                          type="text"
                          [ngModel]="cond.value_manual"
                          (ngModelChange)="
                            cond.value_manual = $event; onValueChange(cond)
                          "
                          [ngModelOptions]="{ standalone: true }"
                          [disabled]="!cond.operator"
                          class="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm disabled:bg-slate-50 disabled:text-slate-400 focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter text..."
                        />
                      }
                      <!-- number -->
                      @else if (argType === 'number') {
                        <input
                          type="number"
                          [ngModel]="cond.value_manual"
                          (ngModelChange)="
                            cond.value_manual = $event; onValueChange(cond)
                          "
                          [ngModelOptions]="{ standalone: true }"
                          [disabled]="!cond.operator"
                          class="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm disabled:bg-slate-50 disabled:text-slate-400 focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter number..."
                          step="any"
                        />
                      }
                      <!-- single select -->
                      @else if (argType === 'single_select') {
                        <select
                          [ngModel]="cond.option_id"
                          (ngModelChange)="
                            cond.option_id = $event; onValueChange(cond)
                          "
                          [ngModelOptions]="{ standalone: true }"
                          [disabled]="!cond.operator"
                          class="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm disabled:bg-slate-50 disabled:text-slate-400 focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select Value...</option>
                          @for (
                            opt of getQuestionOptions(
                              cond.condition_element_id
                            );
                            track opt.id
                          ) {
                            <option [value]="opt.id">
                              {{
                                opt.option_display ||
                                  opt.label ||
                                  'Option ' + opt.option_key
                              }}
                            </option>
                          }
                        </select>
                      }
                      <!-- multi select -->
                      @else if (argType === 'multi_select') {
                        <div class="space-y-2">
                          <div
                            class="space-y-2 bg-white border border-slate-200 rounded-lg p-2 max-h-[200px] overflow-y-auto"
                            [class.bg-slate-50]="!cond.operator"
                          >
                            @for (
                              opt of getQuestionOptions(
                                cond.condition_element_id
                              );
                              track opt.id
                            ) {
                              <label
                                class="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded"
                                [class.opacity-50]="!cond.operator"
                              >
                                <input
                                  type="checkbox"
                                  [checked]="isOptionSelected(cond, opt.id)"
                                  (change)="toggleMultiOption(cond, opt.id)"
                                  [disabled]="!cond.operator"
                                  class="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span class="text-sm text-slate-700">
                                  {{
                                    opt.option_display ||
                                      opt.label ||
                                      'Option ' + opt.option_key
                                  }}
                                </span>
                              </label>
                            }
                          </div>
                          @if (
                            cond.option_targets &&
                            cond.option_targets.length > 0
                          ) {
                            <div class="text-xs text-blue-600 mt-1">
                              {{ cond.option_targets.length }} option(s)
                              selected
                            </div>
                          }
                        </div>
                      }
                      <!-- none type -->
                      @else if (argType === 'none') {
                        <div
                          class="px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-600"
                        >
                          value
                        </div>
                      }
                      <!-- fallback -->
                      @else {
                        <div
                          class="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-400"
                        >
                          Select operator first
                        </div>
                      }
                    }
                  }
                </div>
              </div>

              <button
                (click)="removeCondition(cond.id)"
                class="p-2 text-slate-400 hover:text-red-500 rounded-lg transition-all"
              >
                <lucide-angular [img]="Trash2" class="w-4 h-4"></lucide-angular>
              </button>
            </div>
          }
        </div>
      }
    </div>
  </div>
}
